import { TCountry } from "@/shared/types";
import { buildLotPath, buildLotSlug } from '@/utils/auctions/auctionSlugs';

type TAuctionLotSlugInput = Parameters<typeof buildLotSlug>[0];

export const URLBuilder = {
  endpoints: {
    getBlogPostsContent() {
      return '/api/blog-post-page?pLevel=2';
    },
    getVehicleTypes() {
      return '/api/vehicle-types?sort=order:asc'
    },
    getBlogPosts(page: number = 1, pageSize: number = 15) {
      return `/api/blog-posts?pLevel=3&sort=fakePublishedAt:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
    },
    getBlogPost(slug: string) {
      return `/api/blog-posts?filters[slug][$eq]=${slug}&pLevel=2`;
    },
    getFaqItems() {
      return '/api/faq-items?pLevel=2';
    },
    getHomepageContent() {
      return '/api/homepage?pLevel=3';
    },
    getContactPageContent() {
      return '/api/contact-page?pLevel=4';
    },
    getFaqPageContentUrl() {
      return '/api/faq-page?pLevel=5';
    },
    getAboutUsContent() {
      return '/api/about-us?pLevel=5';
    },
    getLocationItem(id: number) {
      return `/api/locations?filters[id][$eq]=${id}&populate[0]=originPort&populate[1]=destinationPort`;
    },
    getBiddingFees(country: TCountry) {
      const countryFilter = country ? `&filters[country][$eq]=${country}` : '';

      return `/api/bidding-fees?pagination[page]=1&pagination[pageSize]=50${countryFilter}`;
    },
    getFinalFees(country: TCountry) {
      const countryFilter = country ? `&filters[country][$eq]=${country}` : '';

      return `/api/final-fees?pagination[page]=1&pagination[pageSize]=50${countryFilter}`;
    },
    getComissionFees() {
      return `/api/comission-fees?pagination[page]=1&pagination[pageSize]=50`;
    },
    getCustomsFees() {
      return `/api/customs-fee`;
    },
    getAdditionalFees() {
      return `/api/additional-fee`;
    },
    getCalculatorContent() {
      return `/api/calculator?pLevel=3`;
    },
    getPrivacyPolicy() {
      return `/api/privacy-policy-page?pLevel=2`;
    },
    getLocations() {
      return `/api/locations?pLevel=2&pagination[page]=1&pagination[pageSize]=500`;
    },
    getCurrencyRates(currency: 'USD' | 'EUR' | 'CAD') {
      return `https://api.nbp.pl/api/exchangerates/rates/a/${currency.toLowerCase()}/?format=json`;
    },
    getCities(page: number = 1, pageSize: number = 100) {
      return `/api/cities?pLevel=2&sort=name:asc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
    },
    getCity(slug: string) {
      return `/api/cities?filters[slug][$eq]=${slug}&pLevel=2`;
    },
    getLocationPageContent() {
      return `/api/location-page?pLevel=2`;
    },
  },
  /**
   * apibara.tech endpoints (spec 007). Paths only — the query string is built by
   * `lotFilters.ts` from a typed allowlist, because this provider ALSO ignores
   * unrecognised filters silently rather than rejecting them.
   *
   * See specs/007-apibara-provider-migration/contracts/apibara-api.md. Two of
   * these differ from the vendor's published documentation.
   */
  apibaraEndpoints: {
    // Overridable so a gate can stand a fake provider in front of the client
    // (a hang, a 502, a malformed body) — the real one cannot be made to fail
    // on demand. Never set in production.
    base: process.env.APIBARA_BASE_URL || 'https://apibara.tech/api/v1/vehicle-auction',
    getVehicles() {
      return '/vehicles';
    },
    /**
     * Previous runs of a vehicle — platform, day, price, the house's status
     * (spec 013). The documented `slug_vin` 404s, exactly as on the detail path.
     *
     * The answer is PER PLATFORM RECORD, not per VIN: by lot number you get that
     * house's runs, by VIN the provider resolves to its preferred record for the
     * VIN — the other house's, when the car has been at both. A full history
     * therefore takes both lookups (measured 2026-09-04 on a Grand Highlander:
     * 4 IAAI runs by lot, 9 Copart runs by VIN). The VIN travels in a
     * server-side request only; it never appears in one of our own URLs.
     */
    getVehicleHistory(lotNumberOrVin: number | string) {
      return `/vehicles/${lotNumberOrVin}/history`;
    },
    /**
     * By LOT NUMBER, not `slug_vin`.
     *
     * The documented path is `/vehicles/{slugVin}`, but every slug returns 404,
     * encoded or not; lot number and VIN both resolve. Lot number is preferred so
     * no VIN appears in a URL.
     *
     * There is no separate "full photo set" endpoint to reach for either — unlike
     * the old provider, `media.items` on the LIST already carries every image.
     */
    getVehicleByLot(lotNumber: number | string) {
      return `/vehicles/${encodeURIComponent(String(lotNumber))}`;
    },
    /** Make/model vocabulary plus every filter's allowed values. */
    getFilters() {
      return '/vehicles/filters';
    },
    /**
     * Facility dictionary — the join that makes prices honest, since `_usd` field
     * names carry CAD for Canadian lots.
     *
     * Page-based and it DOES report a total, unlike `/vehicles`. Defaults to 50
     * rows, so callers must page to `last_page`: reading only the first page once
     * produced a wrong conclusion about Canadian coverage.
     */
    getLocations() {
      return '/locations';
    },
    /** Remaining request allowance (FR-029). */
    getUsage() {
      return '/usage';
    },
  },

  paths: {
    getBlogPost(slug: string) {
      return `/blog/${slug}`;
    },
    getCity(slug: string) {
      return `/obslugiwane-miasta/${slug}`;
    },
    getCitiesHub() {
      return `/obslugiwane-miasta`;
    },
    getBlogPosts(page: number = 1) {
      if (page > 1) {
        return `/blog?page=${page}`;
      }

      return '/blog';
    },
    /**
     * Readable lot URL, nested under the car's make and model (spec 010).
     *
     * There is no bare-lot-number form. `/aukcje/lot/<number>` existed as a
     * retired address to catch links shared before spec 010 moved the page —
     * but the section never reached production under that shape, so there was
     * nothing to catch. The lot object is the only input, which also means a
     * caller cannot build a lot URL without the make and model it needs.
     */
    getAuctionLot(lot: TAuctionLotSlugInput) {
      return `/aukcje/${buildLotPath(lot)}`;
    },
    /**
     * Make and model live in the PATH, everything else in the query string
     * (spec 006, FR-027). Two URLs for one result set would split whatever
     * ranking these pages eventually earn, so the path form is the only one the
     * site links to — `/aukcje?marka=` redirects here.
     */
    getAuctionLotsBase(makeSlug?: string | null, modelSlug?: string | null) {
      if (!makeSlug) return '/aukcje';

      return modelSlug ? `/aukcje/${makeSlug}/${modelSlug}` : `/aukcje/${makeSlug}`;
    },
    /**
     * Preserves active filters across pagination (FR-015/FR-016) — `searchParams`
     * carries the visitor's current filter state.
     */
    getAuctionLots(
      page: number = 1,
      searchParams?: URLSearchParams,
      makeSlug?: string | null,
      modelSlug?: string | null
    ) {
      const params = new URLSearchParams(searchParams);
      params.delete('page');

      // Never both in the path and the query — that is the duplicate this
      // structure exists to remove.
      if (makeSlug) {
        params.delete('marka');
        params.delete('model');
      }

      if (page > 1) params.set('page', String(page));

      const base = URLBuilder.paths.getAuctionLotsBase(makeSlug, modelSlug);
      const query = params.toString();

      return query ? `${base}?${query}` : base;
    },
  },
};